import logger from '@/lib/logger';
import { useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import { ProgressCircle } from '@/features/measurement/ProgressResultsView/components/ProgressCircle';
import { PatientPanel } from '@/features/measurement/ProgressResultsView/components/PatientPanel';
import { SurveyPanel } from '@/features/measurement/ProgressResultsView/components/SurveyPanel';
import { ReferenceLabelsPanel } from '@/features/measurement/ProgressResultsView/components/ReferenceLabelsPanel';
import { useHasAnyRole } from '@/store/useAuthStore';
import { useMeasurementWizardStore } from '@/store/useMeasurementWizardStore';
import { testService } from '@/services/api/testService';

interface StepMeasurementPhaseProps {
  phase: 'incubation' | 'measuring';
  incubationProgress: number;
  measurementProgress: number;
}

export function StepMeasurementPhase({ phase, incubationProgress, measurementProgress }: StepMeasurementPhaseProps) {
  const testId = useMeasurementWizardStore((s) => s.testId);
  const selectedPatientId = useMeasurementWizardStore((s) => s.selectedPatientId);
  const selectedProfile = useMeasurementWizardStore((s) => s.selectedProfile);
  const isResearcher = useHasAnyRole('researcher', 'admin');

  const leftProgress = phase === 'incubation' ? incubationProgress : measurementProgress;
  const leftStatus = phase === 'incubation' ? '(1 of 2) Incubating...' : '(2 of 2) Measuring...';

  // Patient auto-save refs — same pattern as ProgressResultsView
  const originalPatientIdRef = useRef<string | null | undefined>(selectedPatientId);
  const isUnmountingRef = useRef(false);

  useEffect(() => {
    isUnmountingRef.current = false;
    return () => { isUnmountingRef.current = true; };
  }, []);

  // Auto-save patient association when it changes during measurement phase
  useEffect(() => {
    if (!testId || isUnmountingRef.current) return;
    if (selectedPatientId === originalPatientIdRef.current) return;
    // Don't clear a previously-set patient if the panel closes to null
    if (selectedPatientId === null && originalPatientIdRef.current !== null) return;

    const updatePatient = async () => {
      if (isUnmountingRef.current) return;
      try {
        await testService.updateTestPatient(testId, selectedPatientId);
        originalPatientIdRef.current = selectedPatientId;
      } catch (error) {
        logger.error('[StepMeasurementPhase] Failed to update patient association:', error);
      }
    };
    updatePatient();
  }, [selectedPatientId, testId]);

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
      gap: 4,
      py: 3,
    }}>
      {/* Left: phase-aware progress circle */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 2,
      }}>
        <ProgressCircle
          progress={leftProgress}
          status={leftStatus}
          testName="Respiratory Panel"
        />
      </Box>

      {/* Right: patient info, survey, and (researcher-only) reference labels — stays mounted across both phases */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        alignItems: 'stretch',
      }}>
        <Box sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}>
          <PatientPanel />
          <SurveyPanel testId={testId ?? undefined} />
          {isResearcher && (
            <ReferenceLabelsPanel
              testId={testId ?? undefined}
              channels={(selectedProfile as any)?.channels}
              mode="measurement"
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
